import type { SortOrder } from '@vartion/ui'
import { isEqual } from 'lodash-es'

import { IndexedDBCache } from '~/services/cache/IndexedDBCache.ts'
import { InMemoryCache } from '~/services/cache/InMemoryCache.ts'
import type { CaseModel, RiskLevel, SearchResponse } from '~/types.ts'

export interface CaseFilter {
  id: number
  user_id: number
  organization_id: number
  name: string
  filters: CaseFilterFilters
  sort_order: CaseFilterSortOrderItem[]
  created_at: string
  updated_at: string
}

export interface PredefinedCaseFilter {
  id: number
  label: string
  filters: Partial<CaseFilterFilters>
}

export type CustomFilter =
  | 'assigned or collaborated'
  | 'assigned'
  | 'collaborated'
  | 'editable status'
  | 'editable'
  | 'expired passports'
  | 'has updated hits'
  | 'partially resolved cases'
  | 'resolved cases'
  | 'unresolved cases'
  | 'unresolved or hit updates with risk'

const defaultFilters = {
  deleted: false,
  type: [] as string[],
  status: [] as string[],
  confidence: [] as string[],
  group_id: [] as (number | null)[],
  user_id: [] as (number | null)[],
  case_user_user_id: [] as (number | null)[],
  case_group_group_id: [] as (number | null)[],
  created_at: [] as string[],
  updated_at: [] as string[],
  searched_at: [] as string[],
  client_id: [] as number[],
  client_identifier: [] as (string | null)[],
  client_labels: [] as string[],
  client_status: [] as string[],
  client_created_at: [] as string[],
  client_updated_at: [] as string[],
  client_overall_client_risk: [] as RiskLevel[],
  client_country: [] as string[],
  client_nationalities: [] as (string | null)[],
  client_profession: [] as string[],
  client_date_of_birth_age: [] as (number | null)[],
  client_incorporation_date_age: [] as (number | null)[],
  client_risk_indicators: [] as string[],
  client_standard_industry_classifications: [] as string[],
  client_cases_count: [] as (number | null)[],
  client_clients_count: [] as (number | null)[],
  organization_id: [] as number[],
  hit_time_range: [] as string[],
  hit_resolution: [] as string[],
  hit_source: [] as string[],
  hit_risk: [] as string[],
  hit_labels: [] as string[],
  hit_resolved_at: [] as string[],
  hit_resolved_by_user_id: [] as number[],
  uuid: [] as string[],
  id: [] as number[],
  name: '',
  aliases: [] as string[],
  nationalities: [] as (string | null)[],
  country: [] as string[],
  identifier: [] as (string | null)[],
  risk: [] as string[],
  labels: [] as string[],
  custom_filters: [] as CustomFilter[],
  page: 1,
  per_page: 10,
  sort_by: 'updated_at',
  sort_order: 'desc' as SortOrder,
  with: ['hitCounts', 'caseUsers', 'caseGroups', 'labels'],
}

export type CaseFilterFilters = typeof defaultFilters

const defaultSortOrder = [
  'type',
  'status',
  'assignee',
  'collaborators',
  'created_at',
  'updated_at',
  'searched_at',
  'client_id',
  'client_identifier',
  'client_labels',
  'client_status',
  'client_created_at',
  'client_updated_at',
  'client_overall_client_risk',
  'client_country',
  'client_nationalities',
  'client_profession',
  'client_date_of_birth_age',
  'client_incorporation_date_age',
  'client_risk_indicators',
  'client_standard_industry_classifications',
  'client_cases_count',
  'client_clients_count',
  'hit_time_range',
  'hit_resolution',
  'hit_source',
  'hit_risk',
  'hit_labels',
  'hit_resolved_at',
  'hit_resolved_by_user_id',
  'uuid',
  'name',
  'aliases',
  'nationalities',
  'country',
  'identifier',
  'risk',
  'labels',
  'custom_filters',
] as const
export type CaseFilterSortOrderItem = (typeof defaultSortOrder)[number]

const hiddenFilterKeys = new Set(['page', 'per_page', 'sort_by', 'sort_order', 'with', 'organization_id', 'deleted'])

/**
 * A composable to manage cases and filters for them.
 * @param options - Options for the composable.
 * @param options.cacheKey - The key to use for caching the selected filters.
 * @param options.forcedFilters - Filters that will always be applied. These filters cannot be removed by the user and they are not shown or saved when a user saves a filter.
 * Filters that are forced will not be able to be added and you will not be able to select existing filters that have overlap with the forced filters.
 */
export function useCases({ cacheKey, forcedFilters = {} }: { cacheKey?: string; forcedFilters?: Partial<CaseFilterFilters> } = {}) {
  const { filters } = useFilters(defaultFilters, {
    cacheKey,
    cache: InMemoryCache.tags('useCases'),
  })

  // The first fetch should only be performed when all async operations are done.
  const ready = reactive({
    sort: false,
    caseFilters: false,
  })

  const cache = IndexedDBCache.tags('useCases')
  cache.get(`${cacheKey}.sort`).then((sort) => {
    if (sort) {
      // Ensure that we do not reset sort_by and sort_order when the user changes the filters.
      filters.updateDefaults({ sort_by: sort[0], sort_order: sort[1] })
    }
    ready.sort = true
  })
  watch(
    () => [filters.sort_by, filters.sort_order],
    (sort) => {
      cache.put(`${cacheKey}.sort`, sort)
      // Ensure that we do not reset sort_by and sort_order when the user changes the filters.
      filters.updateDefaults({ sort_by: sort[0], sort_order: sort[1] as SortOrder })
    },
  )

  watch(
    () => $route.value.path,
    (path) => {
      forcedFilters.organization_id = path === '/administration/cases' ? [] : [stores.organization.id]
    },
    { immediate: true },
  )

  const predefinedFilters: PredefinedCaseFilter[] = [
    { id: 1, label: $t('unresolved-hits'), filters: { custom_filters: ['unresolved cases', 'partially resolved cases'] } },
    { id: 2, label: $t('in-review-cases'), filters: { status: ['In review'] } },
    { id: 3, label: $t('unassigned-cases'), filters: { user_id: [null] } },
  ]

  const filtersVisible = ref(false)
  const visibleFilters = ref<Set<CaseFilterSortOrderItem>>(new Set())

  const selectedFilter = ref<CaseFilter>()
  watch(selectedFilter, (filter) => cache.put(`${cacheKey}.selectedFilterId`, filter?.id ?? null))

  const caseFilters = ref<CaseFilter[]>([])
  api.post('case-filters/searches', { organization_id: [stores.organization.id], user_id: [stores.user.user.id], page: 1, per_page: 100 }).then(async ({ data }) => {
    caseFilters.value = data.data
    const cachedFilterId = await cache.get<number>(`${cacheKey}.selectedFilterId`)
    if (cachedFilterId) {
      const caseFilter = caseFilters.value.find((filter) => filter.id === cachedFilterId)
      if (forcedFilters.client_id && (caseFilter?.filters.client_id.length || caseFilter?.filters.client_status.length)) {
        resetFilters()
      } else {
        selectedFilter.value = caseFilter
        Object.assign(filters, caseFilter?.filters)
      }
    } else {
      const cachedPredefinedFilterId = await cache.get<number>(`${cacheKey}.selectedPredefinedFilterId`)
      if (cachedPredefinedFilterId) {
        const predefinedFilter = predefinedFilters.find((filter) => filter.id === cachedPredefinedFilterId)
        if (predefinedFilter) {
          Object.assign(filters, predefinedFilter.filters)
        }
      }
    }

    ready.caseFilters = true

    // We need to refresh the visible filters regardless of the selected filter.
    // This is because the cached filters might not be a saved `CaseFilter`.
    refreshVisibleFilters()
    filtersVisible.value = visibleFilters.value.size > 0
  })

  // A user can select filters in a specific order. We want to keep that order when displaying the filters.
  // `CaseFilter`s will store the order of the filters and when they are loaded, the visibleFilters will be updated to match the order.
  const sortedKeys = computed(() => {
    const keys = new Set<CaseFilterSortOrderItem>()
    for (const key of visibleFilters.value) {
      keys.add(key)
    }

    if (selectedFilter.value) {
      for (const key of selectedFilter.value.sort_order) {
        keys.add(key)
      }
    }

    for (const key of defaultSortOrder) {
      if (!keys.has(key)) {
        keys.add(key)
      }
    }

    return keys
  })

  const activeFilters = computed(() => {
    const active: Partial<CaseFilterFilters> = {}
    for (const [key, value] of Object.entries(filters.toJson())) {
      if (hiddenFilterKeys.has(key)) {
        continue
      }

      const isActive = Array.isArray(value) ? value.length > 0 : !!value
      if (isActive) {
        // @ts-expect-error - active is a partial of filters.
        active[key] = value
      }
    }

    return active
  })

  const filtersActive = computed(() => Object.keys(activeFilters.value).length > 0)

  const hasChanges = computed(() => {
    if (!selectedFilter.value) {
      return Object.keys(activeFilters.value).length > 0
    }

    for (const key in selectedFilter.value.filters) {
      // @ts-expect-error - keys are the same.
      if (!isEqual(filters[key], selectedFilter.value.filters[key])) {
        return true
      }
    }

    return !isEqual(
      Array.from(sortedKeys.value).filter((value) => value !== 'name'),
      Array.from(selectedFilter.value.sort_order).filter((value) => value !== 'name'),
    )
  })

  const selectedPredefinedFilter = computed(() => predefinedFilters.find((filter) => isEqual(sortObjectByKeys(activeFilters.value), sortObjectByKeys(filter.filters))))
  watch(selectedPredefinedFilter, (filter) => cache.put(`${cacheKey}.selectedPredefinedFilterId`, filter?.id ?? null))

  const isReady = computed(() => Object.values(ready).every((value) => value))

  /**
   * The inputs that will be sent to the API.
   * If defaults are provided, they will be merged with the filters.
   */
  const inputs = computed((oldValue): Record<string, any> | undefined => {
    if (!isReady.value) {
      return oldValue // By returning the old value, useFetch is not triggered.
    }

    const inputs = {
      deleted: filters.deleted,
      type: filters.type,
      status: filters.status,
      confidence: filters.confidence,
      group_id: filters.group_id,
      user_id: filters.user_id,
      created_at: filters.created_at,
      updated_at: filters.updated_at,
      searched_at: filters.searched_at,
      organization_id: filters.organization_id,
      uuid: filters.uuid,
      id: filters.id,
      name: filters.name ? `%${filters.name}%` : undefined,
      aliases: filters.aliases,
      nationalities: filters.nationalities,
      country: filters.country,
      identifier: filters.identifier,
      risk: filters.risk,
      labels: filters.labels,
      custom_filters: filters.custom_filters,
      page: filters.page,
      per_page: filters.per_page,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      with: filters.with,

      case_users: {
        user_id: filters.case_user_user_id,
      },

      case_groups: {
        group_id: filters.case_group_group_id,
      },

      client_status: filters.client_status,
      client_id: filters.client_id,
      clients: {
        cases_count: filters.client_cases_count,
        clients_count: filters.client_clients_count,
        created_at: filters.client_created_at,
        updated_at: filters.client_updated_at,
        overall_client_risk: filters.client_overall_client_risk,
        country: filters.client_country,
        nationalities: filters.client_nationalities,
        profession: filters.client_profession.map((profession) => `%${profession}%`),
        date_of_birth_age: filters.client_date_of_birth_age,
        incorporation_date_age: filters.client_incorporation_date_age,
        risk_indicators: filters.client_risk_indicators,
        client_standard_industry_classifications: filters.client_standard_industry_classifications,
        labels: filters.client_labels,
        identifier: filters.client_identifier,
      },

      hit_time_range: filters.hit_time_range,
      hit_resolution: filters.hit_resolution,
      hit_source: filters.hit_source,
      hits: {
        risk: filters.hit_risk,
        labels: filters.hit_labels,
        resolved_at: filters.hit_resolved_at,
        resolved_by_user_id: filters.hit_resolved_by_user_id,
      },

      ...forcedFilters,
    }

    if (app.config.globalProperties.$inAdministration) {
      inputs.with.push('organization')
    }

    return inputs
  })

  const { data, loading, meta, fetch } = useFetch<SearchResponse<CaseModel<['clients']>>>('cases/searches', {
    inputs,
    deep: true,
    debounce: 300,
    pagination: true,
  })

  const searching = computed(() => data.value.filter((row) => row.id in stores.runningIntelligentCheckJobs.runningIntelligentCheckJobs).map((row) => row.id))
  watch(
    searching,
    (value, oldValue) => {
      if (value.length < oldValue.length) {
        fetch()
      }
    },
    { deep: true },
  )

  async function onUpdateFilters({ updates, reset, show }: { updates: Partial<CaseFilterFilters>; reset?: boolean; show?: boolean }) {
    while (Object.values(ready).some((value) => !value)) {
      // Wait for all async operations to finish.
      await delay(100)
    }

    if (reset) {
      resetFilters()
    }

    Object.assign(filters, updates)
    refreshVisibleFilters()

    if (show) {
      filtersVisible.value = true
    }
  }

  function resetFilters() {
    filters.reset()
    selectedFilter.value = undefined
    visibleFilters.value.clear()
  }

  const bus = useBus()

  bus.on('cases.fetch', fetch)
  bus.on('cases.updateFilters', onUpdateFilters)

  /**
   * Refresh the visible filters based on the selected filter.
   */
  function refreshVisibleFilters() {
    const sortOrder = selectedFilter.value?.sort_order ?? defaultSortOrder

    visibleFilters.value = new Set(
      sortOrder.filter((key) => {
        if (key in forcedFilters || key === 'name') return false

        if (key === 'assignee') {
          return 'user_id' in activeFilters.value || 'group_id' in activeFilters.value
        }

        if (key === 'collaborators') {
          return 'case_user_user_id' in activeFilters.value || 'case_group_group_id' in activeFilters.value
        }

        return key in activeFilters.value
      }),
    )
  }

  function exportFilters() {
    const exported: Partial<CaseFilterFilters> = filters.toJson()
    for (const key of hiddenFilterKeys) {
      delete exported[key as keyof CaseFilterFilters]
    }
    for (const key in forcedFilters) {
      delete exported[key as keyof CaseFilterFilters]
    }

    return exported
  }

  return {
    activeFilters,
    data,
    defaultSortOrder,
    exportFilters,
    fetch,
    filters,
    filtersActive,
    filtersVisible,
    forcedFilters,
    predefinedFilters,
    selectedPredefinedFilter,
    hasChanges,
    inputs,
    isReady,
    loading,
    meta,
    refreshVisibleFilters,
    resetFilters,
    caseFilters,
    searching,
    selectedFilter,
    sortedKeys,
    visibleFilters,
  }
}
